import { Flex, Modal, Typography } from 'antd';
import { page_couldnt_load } from 'assets';
import { COLOR_OPTIONS } from 'config/style';
import STTypo, { STTypoModels } from 'core/shared/typo';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import { captureError } from 'infra/monitoring/monitoring.utils';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledButton, StyledErrorContainer, StyledTextLink } from './something-went-wrong.styles';

const CHUNK_ERRORS = ['failed to fetch', 'error loading dynamically imported module'];
export const SomethingWentWrong = ({ error, componentStack }: { error: Error; componentStack: string }) => {
  const [t] = useTranslation();
  const isChunkError = new RegExp(CHUNK_ERRORS.join('|'), 'i').test(error.message);

  useEffect(() => {
    if (isChunkError) {
      Modal.info({
        title: t('modals.update.title'),
        content: <Typography.Text>{t('modals.update.description')}</Typography.Text>,
        onOk: () => window.location.reload(),
        okText: t('modals.update.button'),
        centered: true,
        keyboard: false,
        transitionName: '',
        maskTransitionName: ''
      });
    }
  }, [isChunkError, t]);

  const segmentTracking = useSegmentTracking();
  const pathLocation = window?.location?.pathname;
  const screen = pathLocation?.split('/')?.[5] ?? pathLocation;

  useEffect(() => {
    if (!isChunkError) {
      segmentTracking.track(`Whoops Screen - Something went wrong`, { isChunkError, screen });

      captureError(error, {
        componentStack,
        message: error?.message,
        isCrash: true,
        source: 'custom',
        domain: 'ErrorBoundary'
      });
    }
  }, [isChunkError, screen, segmentTracking, error, componentStack]);

  const handleReloadPage = useCallback(() => {
    segmentTracking.track(`Whoops Screen - Clicked button try again`, { isChunkError, screen });
    window.location.reload();
  }, [isChunkError, screen, segmentTracking]);

  if (isChunkError) {
    return null;
  }

  return (
    <StyledErrorContainer data-testid='something-went-wrong-container'>
      <img src={page_couldnt_load} alt='Error illustration' />
      <Flex align='center' justify='center' vertical>
        <STTypo
          type={STTypoModels.TypeOptions.H2}
          color={COLOR_OPTIONS.NEUTRAL_100}
          fontWeight={600}
          fontFamily='Noto Sans'
          lineHeight='48px'>
          {t('whoops.page_could_not_be_loaded.title')}
        </STTypo>
        <STTypo type={STTypoModels.TypeOptions.H4} color={COLOR_OPTIONS.NEUTRAL_90} fontFamily='Noto Sans' lineHeight='32px'>
          {t('whoops.page_could_not_be_loaded.description')}
        </STTypo>
        <STTypo type={STTypoModels.TypeOptions.H4} color={COLOR_OPTIONS.NEUTRAL_90} fontFamily='Noto Sans' lineHeight='32px'>
          {t('whoops.page_could_not_be_loaded.please_try_again')}
        </STTypo>
      </Flex>

      <StyledButton size='large' type='primary' onClick={handleReloadPage}>
        {t('report.try_again')}
      </StyledButton>

      <Flex align='center' justify='center' vertical>
        <STTypo color={COLOR_OPTIONS.NEUTRAL_90} fontFamily='Noto Sans' lineHeight='32px'>
          {t('whoops.whoops.automaticamente')}{' '}
          <StyledTextLink href='#/' data-testid='whoops-open'>
            {t('whoops.whoops.btn')}
          </StyledTextLink>{' '}
          {t('whoops.whoops.help')}
        </STTypo>
      </Flex>
    </StyledErrorContainer>
  );
};
